import usePosOrderMenuValues from "@/shared/atoms/usePosOrderMenuValuesAtom";
import { useSearchText } from "@/shared/atoms/usePosOrderSearchAtom";
import useCategoryQueryHook from "@/shared/hooks/useCategoryQueryHook";
import {
  filterMenuItemByName,
  isFavoriteCategory,
  sortFavoriteCategoriesFirst,
} from "@/shared/utils/helpers";
import { Fragment, useMemo } from "react";
import PosMenuItem from "./PosMenuItem";
import { PosSettings } from "@/shared/utils/interfaces";
import { cn } from "@/shared/utils/cn";

const CategoryCardStandard = ({ settings }: { settings: PosSettings }) => {
  const { categories, products } = useCategoryQueryHook();
  const [{ selectedCat, selectedTab }] = usePosOrderMenuValues();
  const text = useSearchText();

  const {
    product_column_count,
    category_column_count,
    column_count_per_category,
  } = settings;

  const filteredCategories = useMemo(
    () =>
      sortFavoriteCategoriesFirst(
        (categories ?? []).filter((cat) => filterMenuItemByName(cat.name, text))
      ),
    [categories, text]
  );

  const filteredSelectedCatProducts = useMemo(
    () =>
      selectedCat?.sub_content?.filter((product) =>
        filterMenuItemByName(product.name, text)
      ) || [],
    [selectedCat?.sub_content, text]
  );

  const filteredProducts = useMemo(
    () =>
      products?.filter((product) => filterMenuItemByName(product.name, text)) ||
      [],
    [products, text]
  );

  return (
    <div className="h-full overflow-y-auto">
      {!selectedCat && selectedTab === 0 ? (
        <div
          className={cn(
            "grid gap-2 grid-cols-2 mt-4",
            `md:grid-cols-${category_column_count}`
          )}
        >
          {filteredCategories.map((cat, index) => (
            <PosMenuItem
              key={cat.item_id}
              item={cat}
              isFavorite={isFavoriteCategory(cat.virtual_item_type)}
              settings={settings}
              bgColor={settings.color_palette[index % 8]}
            />
          ))}
        </div>
      ) : null}

      <div
        className={cn(
          `grid gap-2 grid-cols-2 mt-4`,
          `md:grid-cols-${column_count_per_category?.[selectedCat?.item_id] || product_column_count}`,
          !selectedCat &&
            text?.length > 1 &&
            "border-t-6 border-dashed border-border pt-4"
        )}
      >
        {selectedCat
          ? filteredSelectedCatProducts.map((product, index) => (
              <Fragment key={product.item_id}>
                <PosMenuItem
                  item={product}
                  settings={settings}
                  bgColor={settings.color_palette[index % 8]}
                />
                {filteredSelectedCatProducts.length === index + 1 && (
                  <PosMenuItem
                    isLast
                    item={{ ...product, parent_id: selectedCat.item_id }}
                    settings={settings}
                    bgColor={settings.color_palette[index % 8]}
                  />
                )}
              </Fragment>
            ))
          : (selectedTab === 1 || text?.length > 1) &&
            filteredProducts.map((product, index) => (
              <PosMenuItem
                key={product.item_id}
                item={product}
                settings={settings}
                bgColor={settings.color_palette[index % 8]}
              />
            ))}
      </div>
    </div>
  );
};

export default CategoryCardStandard;
